import React, { useState, useEffect } from 'react'
import { Stack } from '@hub/stack'
import { H3 } from '@hub/heading'
import { Text } from '@hub/text'
import { Link, LinkOverlay } from '@hub/link'
import { Grid } from '@hub/grid'
import {
  DEFAULT_BREAKPOINT,
  HubStyleObject,
  useBreakpoint,
  As,
  useToken,
} from '@hub/design-system-base'
import { CoreContainer } from '@hub/core-container'
import {
  SGPageTemplateCallToAction,
  SGPageTemplateImage,
} from '../../../types/page-templates'
import { CloudinaryImage } from '../cloudinary-image'

interface ColorCardsProps {
  title?: string
  description?: string
  sx?: HubStyleObject
  hasOuterMargins?: boolean
}

interface ColorCardProps {
  isFullWidth?: boolean
  sx?: HubStyleObject
  title: string
  image: SGPageTemplateImage
  shortDescription?: string | null
  callToAction?: SGPageTemplateCallToAction | null
  as?: As
}

const ColorCardContent: React.FC<
  Pick<
    ColorCardProps,
    'isFullWidth' | 'title' | 'shortDescription' | 'callToAction'
  >
> = ({ isFullWidth, title, shortDescription, callToAction }) => (
  <>
    <Stack sx={{ ...(isFullWidth ? { width: '70%', marginX: 'auto' } : {}) }}>
      <Text lineHeight="base" noOfLines={2} tone="strong">
        {title}
      </Text>
      <Text lineHeight="base" noOfLines={3}>
        {shortDescription}
      </Text>
    </Stack>
    {callToAction && (
      <Text>
        <Link hasUnderline href={callToAction.url}>
          {callToAction.label}
        </Link>
      </Text>
    )}
  </>
)

export const ColorCards: React.FC<ColorCardsProps> = ({
  title,
  description,
  children,
  hasOuterMargins = true,
}) => {
  const breakpoint = useBreakpoint(DEFAULT_BREAKPOINT)

  return (
    <CoreContainer gutter={hasOuterMargins ? undefined : 'size-none'}>
      <Stack
        gap="spacing-sm"
        align={['center', 'center', 'unset']}
        sx={
          hasOuterMargins ? { marginX: [null, null, null, '10%'] } : undefined
        }
      >
        {/* custom inset chosen by design to create visual rhythm on lg, xl, 2xl */}
        {title || description ? (
          <Stack align="center">
            {title && <H3>{title}</H3>}
            {description && (
              <Text color="quartz" align="center" sx={{ maxWidth: '60ch' }}>
                {description}
              </Text>
            )}
          </Stack>
        ) : null}

        <Grid templateColumns={['1fr', null, '1fr 1fr']} gap="spacing-md">
          {/* if there is only 1 child, show it full width and make it span two columns on desktop/tablet */}
          {React.Children.map(children, child => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, {
                isFullWidth:
                  React.Children.count(children) === 1 &&
                  ['2xl', 'xl', 'lg', 'md'].includes(
                    breakpoint || DEFAULT_BREAKPOINT
                  ),
                sx: {
                  ...(React.Children.count(children) === 1 &&
                  ['2xl', 'xl', 'lg', 'md'].includes(
                    breakpoint || DEFAULT_BREAKPOINT
                  )
                    ? {
                        ...(child.props.sx ? child.props.sx : {}),
                        gridColumn: ['span 1', null, 'span 2'],
                      }
                    : {}),
                },
              })
            } else {
              return child
            }
          })}
        </Grid>
      </Stack>
    </CoreContainer>
  )
}

export const ColorCard: React.FC<ColorCardProps> = ({
  isFullWidth = false,
  title,
  image,
  shortDescription,
  callToAction,
  sx,
  as,
}) => {
  const defaultSupportingColor = useToken('colors', 'smoke')
  const squareImagePadding = useToken('space', 'spacing-md')
  const supportingColour = image.supportingColour || defaultSupportingColor
  const [width, setWidth] = useState<number | undefined>()
  useEffect(() => {
    function updateSize(): void {
      if (window) {
        setWidth(window.innerWidth)
      }
    }
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [])
  return (
    <Stack
      sx={{
        backgroundColor: supportingColour,
        padding: 'spacing-md',
        maxWidth: ['456px', null, 'size-full'],
        minWidth: [
          `calc(${width}px - (5 * ${squareImagePadding}))`,
          '424px',
          'auto',
        ],
        ...sx,
      }}
      as={as}
      gap="spacing-md"
      shouldWrapChildren={false}
      direction={isFullWidth ? 'row' : 'column'}
    >
      {callToAction ? (
        <Stack
          sx={{
            position: 'relative',
            ...(isFullWidth ? { flexBasis: '33%' } : {}),
          }}
        >
          <LinkOverlay href={callToAction.url}>
            <CloudinaryImage
              ratio="1"
              imageSetOrImage={image}
              maxWidth="456px"
              minWidth={[
                `calc(${width}px - (5 * ${squareImagePadding}))`,
                '424px',
                'auto',
              ]}
            />
          </LinkOverlay>
        </Stack>
      ) : (
        <CloudinaryImage
          ratio="1"
          imageSetOrImage={image}
          sx={{ ...(isFullWidth ? { flexBasis: '33%' } : {}) }}
          maxWidth="456px"
          minWidth={[
            `calc(${width}px - (5 * ${squareImagePadding}))`,
            '424px',
            'auto',
          ]}
        />
      )}
      <Stack
        gap="spacing-xs"
        justify={isFullWidth ? 'center' : 'space-between'}
        sx={{
          ...(isFullWidth
            ? { alignContent: 'center', textAlign: 'center' }
            : { flexGrow: 1, height: 'size-full' }),
        }}
        shouldWrapChildren={false}
      >
        <ColorCardContent
          isFullWidth={isFullWidth}
          title={title}
          shortDescription={shortDescription}
          callToAction={callToAction}
        />
      </Stack>
    </Stack>
  )
}
